import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useWriteScene } from '@/features/scenes/hooks/use-mutation-hooks';
import { NarrativeSummary } from '@/lib/types';

interface UnreadyScenesDialogProps {
  isOpen: boolean;
  scenes: NarrativeSummary[];
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  isAssembling?: boolean;
}

export function UnreadyScenesDialog({
  isOpen,
  scenes,
  onClose,
  onConfirm,
  isAssembling = false,
}: UnreadyScenesDialogProps) {
  const { t } = useTranslation();
  const { writeScene } = useWriteScene();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loading = isSubmitting || isAssembling;

  const handleConfirm = async () => {
    if (loading) return;
    setIsSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('chapters.publish.unready_dialog_title')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t('chapters.publish.unready_dialog_desc')}
        </DialogContentText>
        <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 2, mb: 2 }}>
          <Stack spacing={1.5}>
            {scenes.map((scene) => {
              const phase = (scene.status?.phase || 'idle').toLowerCase();
              return (
                <Box
                  key={scene.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    pb: 1,
                    '&:last-child': { borderBottom: 'none', pb: 0 }
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {scene.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {t('entity_form.scenes.status_label')}: {t(`writers_room.phases.${phase}`)}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    disabled={loading}
                    onClick={() => {
                      onClose();
                      void writeScene(scene);
                    }}
                  >
                    {t('common.edit')}
                  </Button>
                </Box>
              );
            })}
          </Stack>
        </Box>
        <DialogContentText>
          {t('chapters.publish.unready_dialog_confirm')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={() => { void handleConfirm(); }}
          color="primary"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {loading
            ? t('chapters.publish.assembling')
            : t('chapters.publish.unready_dialog_confirm_action')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
